import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  FileText,
  Search,
  Settings,
  Eye,
  Edit,
  Download,
  MoreHorizontal,
  RefreshCw,
  Plus,
  Filter,
  Calendar,
  Building,
  TrendingUp,
  Receipt,
  Wallet,
  CheckCircle2
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { supabase } from '../lib/supabase';
import { useNotifications } from '../store/useStore';
import type { Invoice, Customer } from '../types/database.types';
import { formatCurrency, formatDate } from '../lib/exportUtils';
import { DocumentTypeSelectionModal } from '../components/DocumentTypeSelectionModal';
import { InvoiceFormModal } from '../components/documentForms/InvoiceFormModal';
import { ProformaInvoiceFormModal } from '../components/documentForms/ProformaInvoiceFormModal';
import { InvoiceWithDeliveryFormModal } from '../components/documentForms/InvoiceWithDeliveryFormModal';
import { DDTFormModal } from '../components/documentForms/DDTFormModal';
import { CreditNoteFormModal } from '../components/documentForms/CreditNoteFormModal';
import type { DocumentType } from '../lib/invoiceUtils';
import { InvoiceDetailPage } from './InvoiceDetailPage';
import { InvoicePrintView } from './InvoicePrintView';
import ExportInvoicesModal from '../components/ExportInvoicesModal';
import { SdiStatusBadge } from '../components/SdiStatusBadge';
import { CREDITED_PAYMENT_STATUS, isCreditableDocumentType, signedAmount } from '../lib/creditNotes';
import { cn } from '../lib/cn';

interface InvoiceWithCustomer extends Invoice {
  customers: Customer | null;
}

interface InstallmentRow {
  id: string;
  installment_number: number;
  due_date: string;
  amount: number;
  percentage: number;
  label: string | null;
  payment_status: string;
  paid_at: string | null;
}

export default function InvoicesPage() {
  const { addNotification } = useNotifications();
  const navigate = useNavigate();

  const [flagLoading, setFlagLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);

  const [allInvoices, setAllInvoices] = useState<InvoiceWithCustomer[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [showDocumentTypeModal, setShowDocumentTypeModal] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'view' | 'edit'>('view');
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);

  // ?status=draft: link diretto dal banner "bozze abbonamento" in Dashboard
  const [searchParams] = useSearchParams();
  const [searchNumber, setSearchNumber] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>(() => searchParams.get('status') ?? 'all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [printInvoiceId, setPrintInvoiceId] = useState<string | null>(null);
  const [markingId, setMarkingId] = useState<string | null>(null);

  // Dialog scelta scadenze da incassare (fatture con pagamento multiplo)
  const [collectInv, setCollectInv] = useState<InvoiceWithCustomer | null>(null);
  const [collectRows, setCollectRows] = useState<InstallmentRow[]>([]);
  const [collectSel, setCollectSel] = useState<Record<string, boolean>>({});
  const [collectSaving, setCollectSaving] = useState(false);

  const loadInvoices = useCallback(async () => {
    setListLoading(true);
    let query = supabase
      .from('invoices')
      .select('*, customers(company_name)')
      .order('created_at', { ascending: false });

    if (searchNumber) query = query.ilike('invoice_number', `%${searchNumber}%`);
    if (filterType !== 'all') query = query.eq('document_type', filterType);
    if (filterStatus !== 'all') query = query.eq('status', filterStatus);

    // Add date filtering here if needed, or implement full search logic

    const { data, error } = await query;

    if (error) {
      console.error('Error loading invoices:', error);
      addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare i documenti' });
    } else {
      setAllInvoices(data as InvoiceWithCustomer[]);
    }
    setListLoading(false);
  }, [searchNumber, filterType, filterStatus, addNotification]);

  useEffect(() => {
    if (enabled) {
      loadInvoices();
    }
  }, [enabled, loadInvoices]);

  const handleCloseInvoiceDetail = () => {
    setShowInvoiceDetail(false);
    setSelectedInvoiceId(null);
  };

  const togglePaid = async (inv: InvoiceWithCustomer) => {
    const newPaid = inv.payment_status !== 'paid';
    setMarkingId(inv.id);
    const nowIso = new Date().toISOString();

    const { error } = await supabase
      .from('invoices')
      .update({ payment_status: newPaid ? 'paid' : 'pending' })
      .eq('id', inv.id);

    if (!error) {
      // Allinea le scadenze così l'analisi incassi resta coerente
      await supabase
        .from('invoice_installments')
        .update({ payment_status: newPaid ? 'paid' : 'pending', paid_at: newPaid ? nowIso : null })
        .eq('invoice_id', inv.id);
    }

    if (error) {
      addNotification({ type: 'error', title: 'Errore', message: "Impossibile aggiornare l'incasso" });
    } else {
      addNotification({
        type: 'success',
        title: newPaid ? 'Incassata' : 'Riaperta',
        message: newPaid ? 'Fattura segnata come incassata' : 'Fattura segnata come da incassare',
      });
      setAllInvoices((prev) =>
        prev.map((i) => (i.id === inv.id ? { ...i, payment_status: newPaid ? 'paid' : 'pending' } : i))
      );
    }
    setMarkingId(null);
  };

  // Click su "Segna incassata": se la fattura ha più scadenze chiede quali segnare;
  // se ne ha 0 o 1 non c'è nulla da scegliere → segna tutta la fattura come prima.
  const openCollect = async (inv: InvoiceWithCustomer) => {
    setMarkingId(inv.id);
    const { data, error } = await supabase
      .from('invoice_installments')
      .select('id, installment_number, due_date, amount, percentage, label, payment_status, paid_at')
      .eq('invoice_id', inv.id)
      .order('installment_number', { ascending: true });
    setMarkingId(null);

    if (error) {
      addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare le scadenze' });
      return;
    }

    const rows = (data || []) as InstallmentRow[];
    if (rows.length <= 1) {
      togglePaid(inv);
      return;
    }

    const sel: Record<string, boolean> = {};
    rows.forEach((r) => { sel[r.id] = r.payment_status === 'paid'; });
    setCollectRows(rows);
    setCollectSel(sel);
    setCollectInv(inv);
  };

  const confirmCollect = async () => {
    if (!collectInv) return;
    setCollectSaving(true);
    const nowIso = new Date().toISOString();

    const results = await Promise.all(
      collectRows.map((r) => {
        const paid = !!collectSel[r.id];
        return supabase
          .from('invoice_installments')
          .update({ payment_status: paid ? 'paid' : 'pending', paid_at: paid ? (r.paid_at || nowIso) : null })
          .eq('id', r.id);
      })
    );
    const failed = results.some((r) => r.error);

    const allPaid = collectRows.every((r) => collectSel[r.id]);
    const paidCount = collectRows.filter((r) => collectSel[r.id]).length;

    const { error: invErr } = await supabase
      .from('invoices')
      .update({ payment_status: allPaid ? 'paid' : 'pending' })
      .eq('id', collectInv.id);

    setCollectSaving(false);

    if (failed || invErr) {
      addNotification({ type: 'error', title: 'Errore', message: 'Impossibile aggiornare gli incassi' });
      return;
    }

    setAllInvoices((prev) =>
      prev.map((i) => (i.id === collectInv.id ? { ...i, payment_status: allPaid ? 'paid' : 'pending' } : i))
    );
    addNotification({
      type: 'success',
      title: allPaid ? 'Incassata' : 'Incasso parziale',
      message: `${paidCount} di ${collectRows.length} scadenze segnate come incassate`,
    });
    setCollectInv(null);
    setCollectRows([]);
    setCollectSel({});
  };

  const getDocumentTypeLabel = (type: string | null | undefined): string => {
    switch (type) {
      case 'invoice': return 'Fattura';
      case 'invoice_with_delivery': return 'Fattura accompagnatoria';
      case 'proforma': return 'Pro forma';
      case 'ddt': return 'DDT';
      case 'credit_note': return 'Nota di credito';
      default: return 'Fattura';
    }
  };

  const loadFlag = useCallback(async () => {
    setFlagLoading(true);
    const { data } = await supabase.from('app_settings').select('value').eq('key', 'invoices_enabled').maybeSingle();
    const val = (data as any)?.value?.enabled;
    setEnabled(val === undefined ? true : Boolean(val));
    setFlagLoading(false);
  }, []);

  useEffect(() => {
    loadFlag();
  }, [loadFlag]);

  const filteredInvoices = useMemo(() => {
    let filtered = [...allInvoices];
    if (searchNumber.trim()) {
      const searchLower = searchNumber.toLowerCase().trim();
      filtered = filtered.filter(inv => inv.invoice_number?.toLowerCase().includes(searchLower) || (inv.invoice_number === null && searchLower.includes('bozza')));
    }
    if (filterType !== 'all') filtered = filtered.filter(inv => ((inv as any).document_type || 'invoice') === filterType);
    if (filterStatus !== 'all') filtered = filtered.filter(inv => inv.status === filterStatus);
    if (filterDateFrom) filtered = filtered.filter(inv => new Date(inv.invoice_date) >= new Date(filterDateFrom));
    if (filterDateTo) {
      const toDate = new Date(filterDateTo); toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(inv => new Date(inv.invoice_date) <= toDate);
    }
    return filtered;
  }, [allInvoices, searchNumber, filterType, filterStatus, filterDateFrom, filterDateTo]);

  const stats = useMemo(() => {
    const issued = filteredInvoices.filter(inv => inv.status === 'issued');
    return {
      // Le note di credito riducono il totale emesso
      totalAmount: issued.reduce((sum, inv) => sum + signedAmount((inv as any).document_type, inv.total_amount), 0),
      total: filteredInvoices.length,
      issued: issued.length,
      drafts: filteredInvoices.filter(inv => inv.status === 'draft').length
    };
  }, [filteredInvoices]);

  // Numero della fattura rettificata, per la riga "Rettifica fattura …" delle note di credito
  const invoiceNumberById = useMemo(
    () => new Map(allInvoices.map(inv => [inv.id, inv.invoice_number])),
    [allInvoices]
  );

  const activeFiltersCount = [searchNumber, filterType !== 'all', filterStatus !== 'all', filterDateFrom, filterDateTo].filter(Boolean).length;

  const statusStyles = {
    issued: 'bg-green-500/10 text-green-500 border-green-500/20',
    draft: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    cancelled: 'bg-red-500/10 text-red-500 border-red-500/20'
  };

  const statusLabels = { issued: 'Emessa', draft: 'Bozza', cancelled: 'Annullata' };

  const openDocument = (id: string) => {
    setSelectedInvoiceId(id);
    setViewMode('view');
    setShowInvoiceDetail(true);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-blue-900">
            Fatture e Documenti
          </h1>
          <p className="text-sm text-neutral-400 mt-1">Gestisci la fatturazione e i documenti commerciali</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => loadInvoices()} className="h-10">
            <RefreshCw className={cn("w-4 h-4", listLoading && "animate-spin")} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-10">
                <Settings className="w-4 h-4 mr-2" /> Azioni
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowExportModal(true)}><Download className="w-4 h-4 mr-2" /> Esporta</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/document-configuration')}><Settings className="w-4 h-4 mr-2" /> Configurazione</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => setShowDocumentTypeModal(true)} className="h-10 px-4 shadow-lg shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white flex-1 md:flex-none">
            <Plus className="w-4 h-4 mr-2" /> Nuovo Documento
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {flagLoading ? (
          [...Array(4)].map((_, i) => <Card key={i} className="h-24 animate-pulse bg-gray-50 border-gray-100" />)
        ) : (
          [
            { label: 'Totale Emesso', value: formatCurrency(stats.totalAmount), icon: TrendingUp, color: 'text-green-500' },
            { label: 'Documenti', value: stats.total, icon: Receipt, color: 'text-blue-500' },
            { label: 'Emessi', value: stats.issued, icon: FileText, color: 'text-green-500' },
            { label: 'Bozze', value: stats.drafts, icon: FileText, color: 'text-yellow-500' }
          ].map((kpi, i) => (
            <Card key={i} className="border-gray-200 bg-white">
              <CardContent className="p-3 md:p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] md:text-sm font-medium text-neutral-500 uppercase tracking-wider">{kpi.label}</p>
                  <kpi.icon className={cn("h-3 w-3 md:h-4 md:w-4", kpi.color)} />
                </div>
                <div className="text-sm md:text-2xl font-bold text-gray-900 truncate">{kpi.value}</div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col gap-3">
        <Card className="p-1">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <Input
                placeholder="Cerca per numero..."
                value={searchNumber}
                onChange={(e) => setSearchNumber(e.target.value)}
                className="pl-10 h-10 md:h-12 border-none bg-transparent focus-visible:ring-0 text-base"
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => setShowFilters(!showFilters)}
              className={cn("h-10 px-3", showFilters && "bg-gray-100 text-primary-600")}
            >
              <Filter className="w-4 h-4 mr-2" />
              {activeFiltersCount > 0 && <span className="mr-2 px-1.5 py-0.5 rounded-full bg-primary-500 text-white text-[10px]">{activeFiltersCount}</span>}
              Filtri
            </Button>
          </div>
        </Card>

        {showFilters && (
          <Card className="p-4 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-xs text-neutral-500 uppercase tracking-wider">Tipo Documento</Label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="bg-white border-gray-200 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="invoice">Fattura</SelectItem>
                    <SelectItem value="invoice_with_delivery">Fattura accompagnatoria</SelectItem>
                    <SelectItem value="credit_note">Nota di credito</SelectItem>
                    <SelectItem value="proforma">Pro forma</SelectItem>
                    <SelectItem value="ddt">DDT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-neutral-500 uppercase tracking-wider">Stato</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="bg-white border-gray-200 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="draft">Bozza</SelectItem>
                    <SelectItem value="issued">Emessa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-neutral-500 uppercase tracking-wider">Data Da</Label>
                <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="bg-white border-gray-200 h-9" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-neutral-500 uppercase tracking-wider">Data A</Label>
                <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="bg-white border-gray-200 h-9" />
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
              <Button variant="ghost" size="sm" onClick={() => { setSearchNumber(''); setFilterType('all'); setFilterStatus('all'); setFilterDateFrom(''); setFilterDateTo(''); }}>Reset</Button>
              <Button size="sm" onClick={() => setShowFilters(false)}>Applica</Button>
            </div>
          </Card>
        )}
      </div>

      {/* Invoices List */}
      <div className="grid grid-cols-1 gap-4">
        {listLoading && allInvoices.length === 0 ? (
          [...Array(4)].map((_, i) => <Card key={i} className="h-24 animate-pulse bg-gray-50 border-gray-100" />)
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-12 rounded-xl">
            <FileText className="w-12 h-12 text-neutral-600 mx-auto mb-4 opacity-20" />
            <p className="text-neutral-400">Nessun documento trovato</p>
          </div>
        ) : (
          filteredInvoices.map((inv) => {
            const isPaid = inv.payment_status === 'paid';
            const docType = ((inv as any).document_type as string) || 'invoice';
            const canCollect = inv.status === 'issued' && isCreditableDocumentType(docType);
            const isCreditNote = docType === 'credit_note';
            const isCredited = inv.payment_status === CREDITED_PAYMENT_STATUS;
            const showSdi = inv.status === 'issued' && (canCollect || isCreditNote);
            const referenceId = (inv as any).reference_invoice_id as string | null;
            const referenceNumber = isCreditNote && referenceId ? invoiceNumberById.get(referenceId) : null;
            return (
            <Card
              key={inv.id}
              className={cn(
                'group p-4 hover:shadow-xl transition-all duration-300 cursor-pointer relative overflow-hidden',
                isCreditNote ? 'border-rose-200 hover:border-rose-400 bg-rose-50/40' : 'border-gray-200 hover:border-primary-500 bg-white'
              )}
              onClick={() => { setSelectedInvoiceId(inv.id); setViewMode('view'); setShowInvoiceDetail(true); }}
            >
              {/* Le note di credito hanno la striscia rossa sempre visibile, le fatture solo al passaggio del mouse */}
              <div className={cn(
                'absolute top-0 left-0 w-1 transition-all duration-300',
                isCreditNote ? 'h-full bg-rose-500' : 'h-0 group-hover:h-full bg-primary-500'
              )} />

              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
                <div className="flex-1 min-w-0 basis-1/2 sm:basis-auto">
                  <h3 className="font-bold text-lg text-gray-900 group-hover:text-primary-600 transition-colors truncate">
                    {inv.invoice_number || <span className="text-neutral-500 italic">Bozza</span>}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <div className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border", statusStyles[inv.status as keyof typeof statusStyles])}>
                      {statusLabels[inv.status as keyof typeof statusLabels]}
                    </div>
                    {isCreditNote ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 font-bold uppercase">
                        {getDocumentTypeLabel(docType)}
                      </span>
                    ) : (
                      <span className="text-[10px] text-neutral-500 font-medium uppercase tracking-tight">
                        {getDocumentTypeLabel(docType)}
                      </span>
                    )}
                    {referenceNumber && (
                      <span className="text-[11px] text-rose-700">Rettifica fattura {referenceNumber}</span>
                    )}
                    {(inv as any).deposit_percentage != null && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 font-bold uppercase">
                        Acconto {(inv as any).deposit_percentage}%
                      </span>
                    )}
                    {(inv as any).parent_invoice_id && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 font-bold uppercase">
                        Saldo
                      </span>
                    )}
                    {(inv as any).price_list_id && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-semibold">
                        da Offerta
                      </span>
                    )}
                  </div>
                </div>

                {showSdi && (
                  <div className="flex-shrink-0 order-last w-full sm:order-none sm:w-auto">
                    <SdiStatusBadge sdiStatus={(inv as any).sdi_status} sdiUuid={(inv as any).sdi_uuid} />
                  </div>
                )}

                {canCollect && isCredited && (
                  <div className="flex-shrink-0 order-last w-full sm:order-none sm:w-auto">
                    <span
                      title="Stornata del tutto da note di credito: non c'è nulla da incassare"
                      className="inline-flex items-center justify-center gap-1 h-9 px-4 w-full sm:w-auto rounded-full border border-rose-300 bg-rose-50 text-rose-700 text-sm font-semibold whitespace-nowrap"
                    >
                      Stornata
                    </span>
                  </div>
                )}

                {canCollect && !isCredited && (
                  <div className="flex-shrink-0 order-last w-full sm:order-none sm:w-auto" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant={isPaid ? 'default' : 'outline'}
                      size="sm"
                      disabled={markingId === inv.id}
                      onClick={(e) => { e.stopPropagation(); openCollect(inv); }}
                      className={cn(
                        'h-9 px-4 w-full sm:w-auto rounded-full font-semibold whitespace-nowrap transition-colors',
                        isPaid
                          ? 'bg-green-600 hover:bg-green-700 text-white border-green-600'
                          : 'border-green-500 text-green-600 hover:bg-green-50'
                      )}
                    >
                      {markingId === inv.id ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : isPaid ? (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      ) : (
                        <Wallet className="w-4 h-4 mr-2" />
                      )}
                      {isPaid ? 'Incassata' : 'Segna incassata'}
                    </Button>
                  </div>
                )}

                <div className="text-right flex flex-col items-end gap-2 flex-shrink-0 sm:flex-1 min-w-0">
                  <div className={cn('text-lg font-black', isCreditNote ? 'text-rose-700' : 'text-gray-900')}>
                    {isCreditNote && '−'}{formatCurrency(inv.total_amount || 0)}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-teal-600 hover:bg-teal-50 rounded-full"
                      title="Anteprima A4"
                      onClick={(e) => { e.stopPropagation(); setPrintInvoiceId(inv.id); }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-neutral-500 hover:bg-gray-100 rounded-full">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setPrintInvoiceId(inv.id); }}><Eye className="w-4 h-4 mr-2" /> Anteprima A4</DropdownMenuItem>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSelectedInvoiceId(inv.id); setViewMode('view'); setShowInvoiceDetail(true); }}><FileText className="w-4 h-4 mr-2" /> Dettaglio</DropdownMenuItem>
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSelectedInvoiceId(inv.id); setViewMode('edit'); setShowInvoiceDetail(true); }}><Edit className="w-4 h-4 mr-2" /> Modifica</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
                <div className="flex items-center text-sm text-neutral-300 min-w-0">
                  <Building className="w-4 h-4 mr-2 text-neutral-500 flex-shrink-0" />
                  <span className="truncate">{inv.customers?.company_name || 'N/A'}</span>
                </div>
                <div className="flex items-center text-sm text-neutral-300">
                  <Calendar className="w-4 h-4 mr-2 text-neutral-500 flex-shrink-0" />
                  <span>{formatDate(inv.invoice_date)}</span>
                </div>
              </div>
            </Card>
            );
          })
        )}
      </div>

      <DocumentTypeSelectionModal
        isOpen={showDocumentTypeModal}
        onClose={() => setShowDocumentTypeModal(false)}
        onDocumentTypeSelect={(type) => { setSelectedDocumentType(type); setShowDocumentTypeModal(false); }}
      />

      <InvoiceFormModal isOpen={selectedDocumentType === 'invoice'} onClose={() => setSelectedDocumentType(null)} onCreated={() => { setSelectedDocumentType(null); loadInvoices(); }} />
      <ProformaInvoiceFormModal isOpen={selectedDocumentType === 'proforma'} onClose={() => setSelectedDocumentType(null)} onCreated={() => { setSelectedDocumentType(null); loadInvoices(); }} />
      <InvoiceWithDeliveryFormModal isOpen={selectedDocumentType === 'invoice_with_delivery'} onClose={() => setSelectedDocumentType(null)} onCreated={() => { setSelectedDocumentType(null); loadInvoices(); }} />
      <DDTFormModal isOpen={selectedDocumentType === 'ddt'} onClose={() => setSelectedDocumentType(null)} onCreated={() => { setSelectedDocumentType(null); loadInvoices(); }} />
      <CreditNoteFormModal isOpen={selectedDocumentType === 'credit_note'} onClose={() => setSelectedDocumentType(null)} onCreated={(nc) => { setSelectedDocumentType(null); loadInvoices(); openDocument(nc.id); }} />

      <InvoiceDetailPage
        isOpen={showInvoiceDetail}
        onClose={handleCloseInvoiceDetail}
        invoiceId={selectedInvoiceId || undefined}
        mode={viewMode}
        onSaveSuccess={loadInvoices}
        onOpenDocument={openDocument}
      />

      <ExportInvoicesModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} />

      {printInvoiceId && (
        <InvoicePrintView
          isOpen={Boolean(printInvoiceId)}
          onClose={() => setPrintInvoiceId(null)}
          invoiceId={printInvoiceId}
        />
      )}

      <Dialog open={collectInv !== null} onOpenChange={(o) => { if (!o && !collectSaving) { setCollectInv(null); setCollectRows([]); setCollectSel({}); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cosa vuoi segnare incassato?</DialogTitle>
            <DialogDescription>
              {collectInv?.invoice_number ? `Fattura ${collectInv.invoice_number}` : 'Fattura'} — seleziona le scadenze incassate.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {collectRows.map((r) => {
              const checked = !!collectSel[r.id];
              return (
                <label
                  key={r.id}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    checked ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setCollectSel((prev) => ({ ...prev, [r.id]: e.target.checked }))}
                    className="w-4 h-4 accent-green-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900">
                      {r.label || `Scadenza ${r.installment_number}`}
                    </div>
                    <div className="text-xs text-neutral-500">
                      Scadenza {formatDate(r.due_date)} · {r.percentage}%
                    </div>
                  </div>
                  <div className="text-sm font-bold text-gray-900 whitespace-nowrap">
                    {formatCurrency(r.amount)}
                  </div>
                </label>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="ghost" disabled={collectSaving} onClick={() => { setCollectInv(null); setCollectRows([]); setCollectSel({}); }}>
              Annulla
            </Button>
            <Button
              disabled={collectSaving}
              onClick={confirmCollect}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {collectSaving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
